
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {FormWizard, TabContent} from "vue-form-wizard";
import NevronInput from "@/components/NevronInput.vue";
import TableGeneric from "@/components/NevronTableGeneric.vue";
import TableModalGeneric from "@/components/NevronTableModalGeneric.vue";
import {SweetModal} from 'sweet-modal-vue';
import stores from "@/stores";
import {Survey} from "@/stores/Surveys";
import {showToaster} from "@/components/DashboardComposer/helpers";
import CreateSurveyQuestions from "@/modules/Surveys/CreateSurveyQuestions.vue";
import {checkTranslationKey, translate} from "@/helpers/functions";
import SmallSkeleton from '@/modules/SmallSkeleton.vue';

@Component({
  methods: {
    checkTranslationKey,
    translate,
    getMinDate() {
      const now = new Date();
      const timeZoneOffset = now.getTimezoneOffset();
      return new Date(now.getTime() - timeZoneOffset * 60 * 1000).toISOString().slice(0, 16);
    }
  },
  components: {
    CreateSurveyQuestions,
    SweetModal,
    FormWizard,
    TabContent,
    NevronInput,
    TableGeneric,
    TableModalGeneric,
    SmallSkeleton,
  }
})
export default class CreateSurvey extends Vue {
  survey: ISurvey = this.initialSurvey();

  wizardKey = 0;
  stores = stores;
  showAttachRecipients = false;
  isLoading: boolean = false;

  prevCheckedOption: string = '';

  display = {
    fixedTo: '',
  };


  @Prop()
  item?: ISurvey;

  get isEditMode(): boolean {
    return !!this.item && !!this.item.id;
  }

  @Watch('item', {immediate: true, deep: true})
  onItemChanged(newVal?: ISurvey, oldVal?: ISurvey) {
    if (!newVal) {
      this.survey = this.initialSurvey();
      return;
    } else {

      this.prevCheckedOption = newVal.scheduleType;

      Vue.set(this, 'survey', new Survey(newVal));


      // We convert the UTC time from db to local

      if (newVal.displayInfinitely) {
        this.display.fixedTo = 'INFINITELY';
      } else {
        this.survey.toDisplayDateTime = this.convertFromUtcToLocal(this.survey.toDisplayDateTime as string);
        this.display.fixedTo = 'FIXED';
      }

      this.survey.fromDisplayDateTime = this.convertFromUtcToLocal(this.survey.fromDisplayDateTime);
    }
  }

  convertFromUtcToLocal(datetime: string): string {
    const convertedDateTime = new Date(datetime + " UTC");
    const month = (convertedDateTime.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = (convertedDateTime.getUTCDay() + 1).toString().padStart(2, '0');

    return `${convertedDateTime.getFullYear()}-${month}-${day} ${convertedDateTime.toLocaleTimeString(navigator.language, {hour12: false})}`;
  }

  onTypeChange() {
    if (!this.isEditMode) {
      this.display.fixedTo = '';
      this.survey.fromDisplayDateTime = '';
      this.survey.toDisplayDateTime = '';
      this.survey.displayFromType = '';
      this.survey.displayInfinitely = false;
    }

    if(this.isEditMode && this.prevCheckedOption === 'TIMED') {
      this.survey.displayFromType = '';
      this.display.fixedTo = '';
    }

    this.prevCheckedOption = this.survey.scheduleType;
  }

  @Watch('display.fixedTo')
  onDisplayOptionToChange() {
    if (this.display.fixedTo === 'INFINITELY') {
      this.survey.toDisplayDateTime = '';
      this.survey.displayInfinitely = true;
    } else {
      this.survey.displayInfinitely = false;
    }
  }

  @Watch('survey.displayFromType')
  onDisplayOptionFromChange() {
    if (this.survey.displayFromType === 'NOW') {
      this.survey.fromDisplayDateTime = new Date().toISOString();
    }
  }

  cancel() {
    this.resetValidation();
    this.showAttachRecipients = false;
    this.survey = this.initialSurvey();
    this.wizardKey++;
    (this.$refs.create as any).close();
  }

  validateForm(formRef: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.$forceUpdate();
      this.$nextTick(() => {
        const form = this.$refs[formRef] as HTMLFormElement;
        form.classList.add('was-validated');
        resolve(form.checkValidity());
      });
    });
  }


  initialSurvey(): ISurvey {
    return new Survey({});
  }

  resetValidation() {
    const refs = this.$refs as any;
    Object.keys(refs).forEach((key) => {
      const ref = refs[key];
      if (ref instanceof HTMLElement) {
        ref.classList.remove('was-validated');
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (!await this.validateForm('formStep1')) return Promise.reject();
    let submitPromise;

    // We convert display times to UTC
    if(this.survey.toDisplayDateTime && !isNaN(Date.parse(this.survey.fromDisplayDateTime))){
      this.survey.fromDisplayDateTime = new Date(this.survey.fromDisplayDateTime).toISOString();

      if(!this.survey.displayInfinitely && this.survey.scheduleType !== 'TIMED') {
        this.survey.toDisplayDateTime = new Date(this.survey.toDisplayDateTime as string).toISOString();
      }
    }

    if (this.isEditMode && this.survey.id) {
      this.checkInsertedValues();
      submitPromise = stores.Surveys.updateItem(this.survey.id, this.survey);
    } else {
      submitPromise = stores.Surveys.createNewItem(this.survey);
    }
    this.isLoading = true;
    return submitPromise.then((response) => {
      if (response.error) {
        throw new Error(response.error.message);
      }
      showToaster('success', '', Vue.prototype.translate('successfully-updated'));
      const eventToEmit = this.isEditMode ? 'save' : 'created';
      this.$emit(eventToEmit, true);
      this.cancel();
    }).catch((error) => {
      let errorMessage = error?.response?.data?.error?.message?.message;
      let errorMessageKey = 'unable-to-perform-action';
      if(errorMessage !== undefined && errorMessage !== ''){
        errorMessageKey = errorMessage
      }
      showToaster('error', 'ERROR:', translate(errorMessageKey));
    }).finally(() => {
      this.isLoading = false;
    });

  }

  async saveAndPublish() {
    if (!await this.validateForm('formStep1'))
      return;
    this.survey.status = 'SCHEDULED';
    if (this.survey.displayFromType === 'NOW') {
      this.survey.status = 'ACTIVE';
    }
    this.onSubmit();
  }

  async saveAsDraft() {
    if (!await this.validateForm('formStep1')){
      return;
    }

    this.survey.status = 'DRAFT';
    this.onSubmit();
  }

  onModalOpen() {
  }

  checkInsertedValues() {
    if(this.survey.scheduleType === 'TIMED'){
      this.survey.toDisplayDateTime = '';
      this.survey.displayInfinitely = false;
      this.survey.numberOfDays = 0;
    }
  }

  // RECIPIENTS
  attachCustomers(customers: ICustomer[]) {
    const union = customers.concat(this.survey.customers.filter((item: ICustomer) => {
      return customers.findIndex((item2: ICustomer) => item.id === item2.id) < 0;
    }));
    this.survey.customers = union;
  }

  detachCustomer(customer: ICustomer) {
    this.survey.customers = this.survey.customers.filter((item: ICustomer) => item.id !== customer.id);
  }

  attachDeviceGroups(deviceGroups: IDeviceGroup[]) {
    const union = deviceGroups.concat(this.survey.deviceGroups.filter((item: IDeviceGroup) => {
      return deviceGroups.findIndex((item2: IDeviceGroup) => item.id === item2.id) < 0;
    }));
    this.survey.deviceGroups = union;
  }

  detachDeviceGroup(deviceGroup: IDeviceGroup) {
    this.survey.deviceGroups = this.survey.deviceGroups.filter((item: IDeviceGroup) => item.id !== deviceGroup.id);
  }

  // DATE TIME
  isDateTimeValid(datetime: string): boolean {
    if (!datetime) return false;

    const timestamp = Date.parse(datetime);
    return !isNaN(timestamp) && new Date(timestamp) > new Date();
  }

  surveyValidity() {
    switch (this.survey.scheduleType) {
      case 'FIXED':
      case 'BEGINNING_OF_STAY':
      case 'END_OF_STAY':
        return this.getValidityOfFixed();
      default:
        return '';
    }
  }

  getValidityOfFixed() {
    const startDate = this.formatDate(this.survey.fromDisplayDateTime);
    const endDate = this.survey.displayInfinitely ? '' : this.formatDate(this.survey.toDisplayDateTime as string);
    return `${startDate} - ${endDate}`;
  }

  formatDate(dateString: string) {
    if (!dateString || !Date.parse(dateString)) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Converts to YYYY-MM-DD format
  }

  surveyDisplayed() {
    switch (this.survey.scheduleType) {
      case 'TIMED':
        return this.formatDate(this.survey.fromDisplayDateTime);
      case 'FIXED':
        return translate('fixed');
      case 'BEGINNING_OF_STAY':
        return translate('beginning-of-stay');
      case 'END_OF_STAY':
        return translate('end-of-stay');
      default:
        return '';
    }
  }

  isValidDateInterval() {
    return new Date(Date.parse(this.survey.toDisplayDateTime as string)) > new Date(Date.parse(this.survey.fromDisplayDateTime));
  }
}
